'use client';
import { createClient } from '@/lib/supabase/client';

export interface LibraryBook {
  id: string;
  title: string;
  author?: string;
  isbn?: string;
  category?: string;
  description?: string;
  coverUrl?: string;
  fileUrl?: string;
  bookType: 'physical' | 'ebook' | 'both';
  totalCopies: number;
  availableCopies: number;
  courseIds: string[];
  publishedYear?: number;
  publisher?: string;
  createdAt: string;
}

export interface BorrowRecord {
  id: string;
  bookId: string;
  bookTitle?: string;
  studentId: string;
  studentNumber: string;
  studentName: string;
  borrowedDate: string;
  dueDate: string;
  returnedDate?: string;
  status: 'borrowed' | 'returned' | 'overdue' | 'lost';
  notes?: string;
  createdAt: string;
}

function dbToBook(row: any): LibraryBook {
  return {
    id: row.id,
    title: row.title,
    author: row.author,
    isbn: row.isbn,
    category: row.category,
    description: row.description,
    coverUrl: row.cover_url,
    fileUrl: row.file_url,
    bookType: row.book_type,
    totalCopies: row.total_copies || 1,
    availableCopies: row.available_copies || 0,
    courseIds: row.course_ids || [],
    publishedYear: row.published_year,
    publisher: row.publisher,
    createdAt: row.created_at,
  };
}

function dbToBorrow(row: any): BorrowRecord {
  return {
    id: row.id,
    bookId: row.book_id,
    bookTitle: row.library_books?.title,
    studentId: row.student_id,
    studentNumber: row.student_number,
    studentName: row.student_name,
    borrowedDate: row.borrowed_date,
    dueDate: row.due_date,
    returnedDate: row.returned_date,
    status: row.status,
    notes: row.notes,
    createdAt: row.created_at,
  };
}

export const libraryService = {
  async getBooks(search?: string): Promise<LibraryBook[]> {
    try {
      const supabase = createClient();
      let query = supabase.from('library_books').select('*').order('title');
      if (search) query = query.ilike('title', `%${search}%`);
      const { data } = await query;
      return (data || []).map(dbToBook);
    } catch { return []; }
  },

  async getBookById(id: string): Promise<LibraryBook | null> {
    try {
      const supabase = createClient();
      const { data } = await supabase.from('library_books').select('*').eq('id', id).single();
      return data ? dbToBook(data) : null;
    } catch { return null; }
  },

  async createBook(book: Omit<LibraryBook, 'id' | 'createdAt'>): Promise<LibraryBook | null> {
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('library_books')
        .insert({
          title: book.title,
          author: book.author,
          isbn: book.isbn,
          category: book.category,
          description: book.description,
          cover_url: book.coverUrl,
          file_url: book.fileUrl,
          book_type: book.bookType,
          total_copies: book.totalCopies,
          available_copies: book.availableCopies,
          course_ids: book.courseIds,
          published_year: book.publishedYear,
          publisher: book.publisher,
        })
        .select()
        .single();
      return data ? dbToBook(data) : null;
    } catch { return null; }
  },

  async updateBook(id: string, updates: Partial<LibraryBook>): Promise<boolean> {
    try {
      const supabase = createClient();
      const payload: any = {};
      if (updates.title !== undefined) payload.title = updates.title;
      if (updates.author !== undefined) payload.author = updates.author;
      if (updates.availableCopies !== undefined) payload.available_copies = updates.availableCopies;
      if (updates.totalCopies !== undefined) payload.total_copies = updates.totalCopies;
      if (updates.fileUrl !== undefined) payload.file_url = updates.fileUrl;
      if (updates.category !== undefined) payload.category = updates.category;
      const { error } = await supabase.from('library_books').update(payload).eq('id', id);
      return !error;
    } catch { return false; }
  },

  async deleteBook(id: string): Promise<boolean> {
    try {
      const supabase = createClient();
      const { error } = await supabase.from('library_books').delete().eq('id', id);
      return !error;
    } catch { return false; }
  },

  async getBorrowingByStudent(studentId: string): Promise<BorrowRecord[]> {
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('book_borrowing')
        .select('*, library_books(title)')
        .eq('student_id', studentId)
        .order('borrowed_date', { ascending: false });
      return (data || []).map(dbToBorrow);
    } catch { return []; }
  },

  async getAllBorrowing(): Promise<BorrowRecord[]> {
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('book_borrowing')
        .select('*, library_books(title)')
        .order('borrowed_date', { ascending: false });
      return (data || []).map(dbToBorrow);
    } catch { return []; }
  },

  async borrowBook(record: Omit<BorrowRecord, 'id' | 'createdAt' | 'bookTitle'>): Promise<BorrowRecord | null> {
    try {
      const supabase = createClient();
      const { data } = await supabase
        .from('book_borrowing')
        .insert({
          book_id: record.bookId,
          student_id: record.studentId,
          student_number: record.studentNumber,
          student_name: record.studentName,
          borrowed_date: record.borrowedDate,
          due_date: record.dueDate,
          status: 'borrowed',
        })
        .select('*, library_books(title)')
        .single();
      // Decrement available copies
      await supabase.rpc('decrement_available_copies', { book_id: record.bookId }).catch(() => {});
      return data ? dbToBorrow(data) : null;
    } catch { return null; }
  },

  async returnBook(borrowId: string, bookId: string): Promise<boolean> {
    try {
      const supabase = createClient();
      const { error } = await supabase
        .from('book_borrowing')
        .update({ status: 'returned', returned_date: new Date().toISOString().split('T')[0] })
        .eq('id', borrowId);
      if (!error) {
        // Increment available copies
        await supabase.rpc('increment_available_copies', { book_id: bookId }).catch(() => {});
      }
      return !error;
    } catch { return false; }
  },
};
